export interface AviationProfileFacilityLike {
  vertical?: string | null;
  facility_type?: string | null;
  facility_type_slug?: string | null;
  primary_profile_slug?: string | null;
  profile_targeting_json?: Record<string, unknown> | null;
  aviation_targeting?: Record<string, unknown> | null;
}

const AVIATION_PROFILE_LABELS: Record<string, string> = {
  aviation_airport_part_139: "Certificated Airport (14 CFR Part 139)",
  aviation_airport_public_use_non_139: "Public-Use Airport (Non-Part 139)",
  aviation_airport_sponsor_grant_assurance: "Airport Sponsor / Grant Assurance",
  aviation_air_operator_part_135: "Charter / On-Demand Operator (14 CFR Part 135)",
  aviation_air_carrier_part_121: "Scheduled Air Carrier (14 CFR Part 121)",
  aviation_repair_station_part_145: "FAA-Certificated Repair Station (14 CFR Part 145)",
  aviation_public_charter_part_380: "Public Charter / Arranger (14 CFR Part 380)",
  aviation_uas_part_107: "Small UAS Operations (14 CFR Part 107)",
  aviation_training_part_141: "Part 141 Pilot School",
  aviation_commercial_space_launch_part_450: "Commercial Space Launch Operator (Part 450)",
  aviation_atc_facility_part_77: "ATC / Airspace Facility",
};

export const formatSlugLabel = (value?: string | null): string =>
  (value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const getAviationProfileSlug = (
  facility?: AviationProfileFacilityLike | null,
): string => {
  const targeting = facility?.profile_targeting_json || facility?.aviation_targeting || {};
  const fromTargeting =
    typeof targeting.primary_profile_slug === "string"
      ? targeting.primary_profile_slug
      : "";
  return (facility?.primary_profile_slug || fromTargeting || "").trim();
};

export const getAviationProfileLabel = (
  facility?: AviationProfileFacilityLike | null,
): string => {
  const slug = getAviationProfileSlug(facility);
  return AVIATION_PROFILE_LABELS[slug] || formatSlugLabel(slug) || "Aviation profile pending";
};

export const getAviationSourceSummary = (
  facility?: AviationProfileFacilityLike | null,
): string => {
  const targeting = facility?.profile_targeting_json || facility?.aviation_targeting || {};
  const source =
    typeof targeting.external_source === "string" ? targeting.external_source : "";
  const sourceLabel =
    source === "faa_nasr_airports"
      ? "FAA NASR/NFDC"
      : formatSlugLabel(source);
  const locationId =
    typeof targeting.certificate_or_location_id === "string"
      ? targeting.certificate_or_location_id
      : "";
  const cycle =
    typeof targeting.cycle_effective_date === "string"
      ? targeting.cycle_effective_date
      : "";
  return [sourceLabel, locationId ? `ID ${locationId}` : "", cycle ? `Cycle ${cycle}` : ""]
    .filter(Boolean)
    .join(" · ");
};

export const isAviationFacility = (
  facility?: AviationProfileFacilityLike | null,
): boolean => (facility?.vertical || "").toLowerCase() === "aviation";

export const hasDeterminedFacilityProfileForTabs = (
  facility?: AviationProfileFacilityLike | null,
): boolean =>
  !!(
    (isAviationFacility(facility) && getAviationProfileSlug(facility)) ||
    (facility?.facility_type_slug ?? "").trim() ||
    (facility?.facility_type ?? "").toString().trim()
  );
